import { Box } from '@mui/material';
import { useStateMachineInput, useRive, Fit, Alignment, Layout } from '@rive-app/react-canvas';
import ROOMS_RIVE from '../../assets/rives/rooms_v2_1.riv?url';
import { useState, useEffect, useCallback } from 'react';
import { throttle } from 'lodash';

// How to build ROOMS_RIVE_HITBOXES:
// 1. Set HITBOX_DEV to true.
// 2. View the page
// 3. Imagine a hitbox on the Rive
// 4. Click the top-left corner of your hitbox.
//    The x/y coordinates will print to the console.
// 5. Record the numbers: x=left, y=top
//    You may round these numbers as you see fit.
//    Nice to know: they are percentages.
// 6. Click the bottom-right corner of your hitbox.
// 7. Record the numbers: x=right, y=bottom
// 8. Put it all in an object in ROOMS_RIVE_HITBOXES
//    and include a `handle` field.
//    Note: you may repeat `handle` fields.
// 9. Repeat steps 3 through 8!
// 10. When finished, set HITBOX_DEV back to false.

const HITBOX_DEV = false;
const IS_REALLY_DEV = import.meta.env.VITE_RUBY_MODE === 'development';

const ROOMS_RIVE_HITBOXES = [
  {
    left: 24,
    top: 17,
    right: 60,
    bottom: 40,
    handle: 'solar',
  },
  {
    left: 76.9,
    top: 59.7,
    right: 78.3,
    bottom: 72.5,
    handle: 'solar',
  },
  {
    left: 19,
    top: 73,
    right: 26,
    bottom: 87,
    handle: 'hvac',
  },
  {
    left: 41,
    top: 74,
    right: 45.5,
    bottom: 84.7,
    handle: 'induction',
  },
  {
    left: 45.5,
    top: 61,
    right: 51,
    bottom: 85,
    handle: 'laundry',
  },
  {
    left: 52.1,
    top: 63,
    right: 57.5,
    bottom: 85,
    handle: 'water',
  },
  {
    left: 60.1,
    top: 63,
    right: 78,
    bottom: 89,
    handle: 'ev',
  },
];

const DEFAULT_ALT_TEXT = "Tips on saving throughout the home. I'm interactive.";

const getMousePosition = (event) => {
  const target = event.target;
  const rect = target.getBoundingClientRect();
  const x = ((event.clientX - rect.left) / rect.width) * 100;
  const y = ((event.clientY - rect.top) / rect.height) * 100;
  return [x, y];
};

const getHitBox = (x, y) => {
  return (
    ROOMS_RIVE_HITBOXES.find(
      ({ left, right, top, bottom }) => left <= x && right >= x && top <= y && bottom >= y,
    ) || null
  );
};

const setInputValue = (input, value) => {
  // Sometimes the Rive runtime goes away, such as
  // during Vite's hotswapping in development mode.
  // So we have to check that the runtime is still
  // there before we can set the value. The `value`
  // field is a magic setter situation that sets
  // a different `value` field, and it *will* throw
  // confusing exceptions.
  if (input.runtimeInput) {
    input.value = value;
  }
};

const InteractiveHome = ({ selected, onClick, rooms }) => {
  const [altTextHandle, setAltTextHandle] = useState('DEFAULT');

  const room = rooms.find((room) => room.handle === altTextHandle);

  const altText = room ? `${room.title}: ${room.description}` : DEFAULT_ALT_TEXT;

  const { RiveComponent, rive } = useRive({
    src: ROOMS_RIVE,
    stateMachines: 'State Machine 1',
    layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
    autoplay: true,
  });

  // Pro tip: If you don't know the input names, ask
  // the developer who made the Rive, or run this line:
  // console.log(rive?.stateMachineInputs('State Machine 1').map(({ name }) => name))
  const inputs = {
    laundry: useStateMachineInput(rive, 'State Machine 1', 'showDryers'),
    solar: useStateMachineInput(rive, 'State Machine 1', 'showSolarBattery'),
    induction: useStateMachineInput(rive, 'State Machine 1', 'showStove'),
    hvac: useStateMachineInput(rive, 'State Machine 1', 'showHeatPump'),
    ev: useStateMachineInput(rive, 'State Machine 1', 'showEVCar'),
    water: useStateMachineInput(rive, 'State Machine 1', 'showWaterHeater'),
  };

  const setActiveInput = useCallback((handle) => {
    Object.values(inputs)
      .filter(Boolean)
      .forEach((input) => setInputValue(input, false));
    if (inputs[handle]) {
      setInputValue(inputs[handle], true);
    }
  }, Object.values(inputs));

  useEffect(() => {
    setActiveInput(selected);
  }, [selected, setActiveInput]);

  const onCanvasClick = (event) => {
    const [x, y] = getMousePosition(event);
    const hitbox = getHitBox(x, y);
    if (hitbox) {
      onClick?.(hitbox.handle);
    }
    if (IS_REALLY_DEV && HITBOX_DEV) {
      console.log({ x, y, hitbox });
    }
  };

  const onMouseOver = () => setActiveInput(null);

  const onMouseOut = () => setActiveInput(selected);

  const onMouseMove = throttle((event) => {
    const [x, y] = getMousePosition(event);
    const hitbox = getHitBox(x, y);
    const handle = hitbox?.handle || 'DEFAULT';
    if (altTextHandle !== handle) {
      setAltTextHandle(handle);
    }
  }, 100);

  return (
    <Box
      display={{
        xs: 'none',
        sm: 'block',
      }}
      textAlign="center"
      mb={2}
      sx={{
        height: '321px',
        backgroundColor: '#F5F5F5',
        position: 'relative',
      }}
      onClick={onCanvasClick}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      onMouseMove={onMouseMove}
    >
      <RiveComponent role="img" aria-label={altText} />
    </Box>
  );
};

InteractiveHome.displayName = 'InteractiveHome';

export default InteractiveHome;
